import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { useLanguage } from "@/contexts/LanguageContext";
import { OPEN_PREFS_EVENT } from "@/lib/consent";
import { TipWidget } from "@/components/tips/TipWidget";
import { PlatformIcon } from "@/lib/platformIcons";
import { BrandMark } from "@/components/common/BrandMark";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Footer() {
  const { t } = useLanguage();
  const year = new Date().getFullYear();
  const [footer, setFooter] = useState({ tagline: null, footer_platform_links: [] });

  useEffect(() => {
    axios.get(`${API}/home/footer`).then((r) => setFooter(r.data)).catch(() => {});
  }, []);

  const cols = [
    { title: t("nav.home"), links: [
      { to: "/", label: t("nav.home") },
      { to: "/pricing", label: t("footer.pricing") },
      { to: "/blog", label: t("footer.blog") },
      { to: "/shop", label: t("nav.shop") },
      { to: "/licensing", label: t("nav.licensing") },
    ] },
    { title: "SAMTWO", links: [
      { to: "/press", label: t("footer.press") },
      { to: "/contact", label: t("footer.contact") },
      { to: "/help", label: t("footer.help") },
    ] },
    { title: t("footer.terms"), links: [
      { to: "/terms", label: t("footer.terms") },
      { to: "/privacy", label: t("footer.privacy") },
      { to: "/refund", label: t("footer.refund") },
      { to: "/cookies", label: t("footer.cookies") },
      { to: "/dmca", label: t("footer.dmca") },
    ] },
  ];

  const links = footer.footer_platform_links || [];

  return (
    <footer data-testid="site-footer" style={{ background: "var(--bg-surface)", borderTop: "1px solid var(--border-color)" }}>
      <div className="chrome-wide max-w-7xl mx-auto px-6 sm:px-8 py-16">
        <div className="mb-12 pb-12 flex justify-center sm:justify-start" style={{ borderBottom: "1px solid var(--border-color)" }} data-testid="footer-tip">
          <TipWidget sourceType="footer" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-[2fr_1fr_1fr_1fr] gap-10">
          <div>
            <BrandMark testId="footer-wordmark" className="mb-3" style={{ width: 220 }} />
            {footer.tagline && (
              <p className="text-sm max-w-xs" style={{ color: "var(--text-secondary)" }} data-testid="footer-tagline">
                {footer.tagline}
              </p>
            )}
            <div className="flex items-center flex-wrap gap-3 mt-6" data-testid="footer-platform-links">
              {links.map((p) => (
                <a
                  key={`${p.platform_name}-${p.sort_order}`}
                  href={p.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={p.platform_name}
                  data-testid={`footer-platform-${p.platform_name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="w-10 h-10 rounded-full flex items-center justify-center transition-all hover:-translate-y-0.5"
                  style={{ background: "var(--bg-subtle)", color: "var(--accent-primary)" }}
                >
                  <PlatformIcon name={p.platform_name} size={20} />
                </a>
              ))}
            </div>
          </div>

          {cols.map((col) => (
            <div key={col.title}>
              <ul className="space-y-3">
                {col.links.map((l) => (
                  <li key={l.to}>
                    <Link to={l.to} data-testid={`footer-link-${l.to.replace(/\//g, "") || "home"}`} className="text-sm transition-colors hover:text-[color:var(--accent-primary)]" style={{ color: "var(--text-secondary)" }}>
                      {l.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-14 pt-8 flex flex-col sm:flex-row items-center justify-between gap-4" style={{ borderTop: "1px solid var(--border-color)" }}>
          <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
            <p className="text-xs" style={{ color: "var(--text-secondary)" }}>
              &copy; {year} SAMTWO, LLC. {t("footer.rights")}
            </p>
            <p className="text-xs" data-testid="footer-legal-line" style={{ color: "var(--text-secondary)" }}>
              <Link to="/terms" data-testid="footer-legal-terms" className="hover:text-[color:var(--accent-primary)]">{t("footer.terms")}</Link>
              {" · "}
              <Link to="/privacy" data-testid="footer-legal-privacy" className="hover:text-[color:var(--accent-primary)]">{t("footer.privacy")}</Link>
              {" · "}
              <Link to="/refund" data-testid="footer-legal-refund" className="hover:text-[color:var(--accent-primary)]">{t("footer.refund")}</Link>
              {" · "}
              <Link to="/cookies" data-testid="footer-legal-cookies" className="hover:text-[color:var(--accent-primary)]">{t("footer.cookies")}</Link>
              {" · "}
              <Link to="/dmca" data-testid="footer-legal-dmca" className="hover:text-[color:var(--accent-primary)]">{t("footer.dmca")}</Link>
            </p>
          </div>
          <button
            type="button"
            data-testid="footer-cookie-preferences"
            onClick={() => window.dispatchEvent(new CustomEvent(OPEN_PREFS_EVENT))}
            className="text-xs transition-colors hover:text-[color:var(--accent-primary)]"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("footer.cookiePreferences")}
          </button>
        </div>
      </div>
    </footer>
  );
}
